import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, BarChart3, GraduationCap, Code2,
  Mic, BookOpen, ArrowRight, Plus, Download, Edit3,
} from 'lucide-react';
import './Dashboard.css';
import TopMenu from '../TopMenu';
import ThreeBackground from './ThreeBackground';
import DashboardWidgets from './DashboardWidgets';
import { getMyResumes } from '../ResumeBuilderPuppeteer/puppeteerApi';
import { getProfile } from '../../services/profileApi';

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const TOOLS = [
  { label: 'AI Resume Builder', desc: 'ATS-ready resumes with AI', icon: FileText, color: '#6366f1', route: '/resume-builder', tag: 'Popular' },
  { label: 'ATS Score Checker', desc: 'Analyze your resume score', icon: BarChart3, color: '#10b981', route: '/ats', tag: null },
  { label: 'Campus to Career', desc: 'College-to-job roadmap', icon: GraduationCap, color: '#a855f7', route: '/campus-to-job', tag: 'New' },
  { label: 'Codeverse', desc: 'Practice coding skills', icon: Code2, color: '#f59e0b', route: '/codeverse', tag: null },
  { label: 'Mock Interview', desc: 'AI interview practice', icon: Mic, color: '#ec4899', route: '/mock-interview', tag: null },
  { label: 'Courses', desc: 'Premium learning paths', icon: BookOpen, color: '#06b6d4', route: '/courses', tag: null },
];

const QUICK = [
  { label: 'Resume', desc: 'Create from scratch', icon: FileText, color: '#6366f1', route: '/resume-builder-v2' },
  { label: 'ATS Check', desc: 'Analyze resume', icon: BarChart3, color: '#10b981', route: '/ats' },
  { label: 'Campus', desc: 'College to career', icon: GraduationCap, color: '#a855f7', route: '/campus-to-job' },
  { label: 'Mock Interview', desc: 'AI practice', icon: Mic, color: '#ec4899', route: '/mock-interview' },
];

const Dashboard = ({ theme = 'dark', toggleTheme }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumes, setResumes] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const dark = theme === 'dark';

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/login'); return; }
    setUser(JSON.parse(userData));
    setLoading(false);
    getMyResumes()
      .then(d => {
        // Handle both { wasSuccessful, data: [...] } and raw array shapes
        const list = Array.isArray(d)
          ? d
          : Array.isArray(d?.data)
            ? d.data
            : Array.isArray(d?.resumes)
              ? d.resumes
              : [];
        setResumes(list);
      })
      .catch((err) => console.error('getMyResumes failed:', err));

    getProfile()
      .then(p => setProfileCompletion(p?.completionPercent ?? 0))
      .catch(() => setProfileCompletion(0));
  }, [navigate]);

  if (loading) return <div className={`d-load ${dark ? 'd-dark' : ''}`}><div className="d-spin" /></div>;

  const name = user?.fullName?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // Show the first two resumes as 3D tablets
  const featured = resumes.slice(0, 2);

  return (
    <div className={`d-page ${dark ? 'd-dark' : 'd-light'}`}>
      <ThreeBackground theme={theme} />
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div className="d-container">

        {/* Hero — LED display pill */}
        <motion.div className="d-hero" variants={fade} initial="hidden" animate="show" custom={0}>
          <div className="d-hero-sheen" />
          <div className="d-hero-inner">
            <p className="d-hero-greet">{greet}</p>
            <h1 className="d-hero-name">Welcome back, {name}</h1>
            <p className="d-hero-sub">Ready to build something amazing today?</p>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div className="d-chips" variants={fade} initial="hidden" animate="show" custom={1}>
          <div className="d-chip d-chip-indigo">
            <span className="d-chip-num">{resumes.length}</span>
            <span className="d-chip-lbl">Resumes</span>
          </div>
          <div className="d-chip d-chip-purple">
            <span className="d-chip-num">{profileCompletion === null ? '…' : `${profileCompletion}%`}</span>
            <span className="d-chip-lbl">Profile</span>
          </div>
          <div className="d-chip d-chip-green">
            <span className="d-chip-num">{resumes.length > 0 ? 'Active' : 'New'}</span>
            <span className="d-chip-lbl">Status</span>
          </div>
        </motion.div>

        {/* 3-column main grid */}
        <div className="d-grid">

          {/* LEFT — Quick actions stacked */}
          <motion.aside className="d-col d-col-left" variants={fade} initial="hidden" animate="show" custom={2}>
            <h3 className="d-col-title">Quick Actions</h3>
            <div className="d-qa-stack">
              {QUICK.map((q) => {
                const I = q.icon;
                return (
                  <div key={q.label} className="d-qa" onClick={() => navigate(q.route)}>
                    <div className="d-qa-ic" style={{ background: q.color, boxShadow: `0 8px 24px ${q.color}44` }}>
                      <I size={22} color="#fff" />
                    </div>
                    <div className="d-qa-body">
                      <h4>{q.label}</h4>
                      <p>{q.desc}</p>
                    </div>
                    <ArrowRight size={16} className="d-qa-arr" />
                  </div>
                );
              })}
            </div>
          </motion.aside>

          {/* CENTER — My Resumes 3D tablets */}
          <motion.section className="d-col d-col-center" variants={fade} initial="hidden" animate="show" custom={3}>
            <h3 className="d-col-title">My Resumes</h3>

            <div className="d-tablets">
              {/* Left tablet */}
              <div className="d-tablet d-tablet-left" onClick={() => featured[0] && navigate(`/resume-builder-v2/editor/${featured[0].templateId}`, { state: { resumeId: featured[0].id } })}>
                <div className="d-tablet-gloss" />
                {featured[0] ? (
                  <>
                    <div className="d-tablet-initial">{(featured[0].firstName?.[0] || '?').toUpperCase()}</div>
                    <div className="d-tablet-meta">
                      <div className="d-tablet-name">{featured[0].firstName} {featured[0].surname}</div>
                      <div className="d-tablet-tpl">{featured[0].templateId}</div>
                    </div>
                  </>
                ) : (
                  <div className="d-tablet-empty">No resumes</div>
                )}
                <div className="d-tablet-btns">
                  <button className="d-tb-btn d-tb-edit" onClick={(e) => { e.stopPropagation(); featured[0] && navigate(`/resume-builder-v2/editor/${featured[0].templateId}`, { state: { resumeId: featured[0].id } }); }}>
                    <Edit3 size={12} /> Edit
                  </button>
                  <button className="d-tb-btn d-tb-dl" onClick={(e) => { e.stopPropagation(); featured[0]?.pdfUrl ? window.open(featured[0].pdfUrl, '_blank') : featured[0] && navigate(`/resume-builder-v2/editor/${featured[0].templateId}`, { state: { resumeId: featured[0].id } }); }}>
                    <Download size={12} /> Download
                  </button>
                </div>
              </div>

              {/* Center + New Resume orb */}
              <button className="d-orb" onClick={() => navigate('/resume-builder-v2')}>
                <div className="d-orb-glow" />
                <Plus size={28} color="#fff" />
                <span className="d-orb-lbl">New Resume</span>
              </button>

              {/* Right tablet */}
              <div className="d-tablet d-tablet-right" onClick={() => featured[1] && navigate(`/resume-builder-v2/editor/${featured[1].templateId}`, { state: { resumeId: featured[1].id } })}>
                <div className="d-tablet-gloss" />
                {featured[1] ? (
                  <>
                    <div className="d-tablet-initial d-init-pink">{(featured[1].firstName?.[0] || '?').toUpperCase()}</div>
                    <div className="d-tablet-meta">
                      <div className="d-tablet-name">{featured[1].firstName} {featured[1].surname}</div>
                      <div className="d-tablet-tpl">{featured[1].templateId}</div>
                    </div>
                  </>
                ) : (
                  <div className="d-tablet-empty">No resume</div>
                )}
                <div className="d-tablet-btns">
                  <button className="d-tb-btn d-tb-edit" onClick={(e) => { e.stopPropagation(); featured[1] && navigate(`/resume-builder-v2/editor/${featured[1].templateId}`, { state: { resumeId: featured[1].id } }); }}>
                    <Edit3 size={12} /> Edit
                  </button>
                  <button className="d-tb-btn d-tb-dl" onClick={(e) => { e.stopPropagation(); featured[1]?.pdfUrl ? window.open(featured[1].pdfUrl, '_blank') : featured[1] && navigate(`/resume-builder-v2/editor/${featured[1].templateId}`, { state: { resumeId: featured[1].id } }); }}>
                    <Download size={12} /> Download
                  </button>
                </div>
              </div>
            </div>

            {resumes.length > 2 && (
              <div className="d-tablets-more" onClick={() => setShowMore(v => !v)}>
                <span>{showMore ? 'Hide extra resumes' : `+${resumes.length - 2} more resumes`}</span>
              </div>
            )}

            {showMore && resumes.length > 2 && (
              <motion.div className="d-more-row" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {resumes.slice(2).map((r, i) => (
                  <div key={r.id || i} className="d-mini" onClick={() => navigate(`/resume-builder-v2/editor/${r.templateId}`, { state: { resumeId: r.id } })}>
                    <div className="d-mini-initial">{(r.firstName?.[0] || '?').toUpperCase()}</div>
                    <div className="d-mini-info">
                      <div className="d-mini-name">{r.firstName} {r.surname}</div>
                      <div className="d-mini-tpl">{r.templateId}</div>
                    </div>
                    <div className="d-mini-btns">
                      <button className="d-tb-btn d-tb-edit" onClick={(e) => { e.stopPropagation(); navigate(`/resume-builder-v2/editor/${r.templateId}`, { state: { resumeId: r.id } }); }}>
                        <Edit3 size={11} />
                      </button>
                      <button className="d-tb-btn d-tb-dl" onClick={(e) => { e.stopPropagation(); r.pdfUrl ? window.open(r.pdfUrl, '_blank') : navigate(`/resume-builder-v2/editor/${r.templateId}`, { state: { resumeId: r.id } }); }}>
                        <Download size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.section>

          {/* RIGHT — Toolkit grid */}
          <motion.aside className="d-col d-col-right" variants={fade} initial="hidden" animate="show" custom={4}>
            <h3 className="d-col-title">Your Toolkit</h3>
            <div className="d-tools">
              {TOOLS.map((t, i) => {
                const I = t.icon;
                return (
                  <motion.div
                    key={t.label}
                    className="d-tool"
                    variants={fade}
                    initial="hidden"
                    animate="show"
                    custom={4.2 + i * 0.05}
                    onClick={() => navigate(t.route)}
                    style={{ '--tool-color': t.color }}
                  >
                    <div className="d-tool-ic" style={{ background: t.color, boxShadow: `0 6px 18px ${t.color}55` }}>
                      <I size={18} color="#fff" />
                    </div>
                    <div className="d-tool-body">
                      <div className="d-tool-head">
                        <h3>{t.label}</h3>
                        {t.tag && <span className="d-tool-tag" style={{ color: t.color, borderColor: t.color }}>{t.tag}</span>}
                      </div>
                      <p>{t.desc}</p>
                    </div>
                    <ArrowRight size={14} className="d-tool-arr" />
                  </motion.div>
                );
              })}
            </div>
          </motion.aside>

        </div>

        <DashboardWidgets dark={dark} />

      </div>
    </div>
  );
};

export default Dashboard;
